// Libs
import { useState, useRef, useEffect, useCallback } from "react";
import {
  PaperAirplaneIcon,
  XMarkIcon,
  PhotoIcon,
  LinkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import ExclamationTriangleIcon from "@heroicons/react/24/outline/ExclamationTriangleIcon";

// Local
import { EmailImagesManager } from "./EmailImagesManager";

interface Message {
  id: string;
  type: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
}

interface Props {
  siteKey: string;
  isEditing: boolean;
  currentHtmlContent: string;
  currentSubject: string;
  conversationID?: string;
  onGenerate: (data: {
    description: string;
    conversationID?: string;
  }) => Promise<{
    htmlContent: string;
    subject: string;
    conversationID: string;
    message?: string;
  }>;
  onEdit: (data: {
    editInstructions: string;
    currentHtmlContent: string;
    currentSubject: string;
    conversationID?: string;
  }) => Promise<{
    htmlContent: string;
    subject: string;
    conversationID: string;
    message?: string;
  }>;
  onApplyChanges: (htmlContent: string, subject: string) => void;
  isLoading: boolean;
  onClose: () => void;
}

export default function StiltonEmailAssistant({
  siteKey,
  isEditing,
  currentHtmlContent,
  currentSubject,
  conversationID,
  onGenerate,
  onEdit,
  onApplyChanges,
  isLoading,
  onClose,
}: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastAIResponse, setLastAIResponse] = useState<{
    htmlContent: string;
    subject: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUserMessage, setLastUserMessage] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInputValue, setUrlInputValue] = useState("");
  const [showImageMenu, setShowImageMenu] = useState(false);
  const imageMenuRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages are added
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Add initial system message
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage: Message = {
        id: `system-${Date.now()}`,
        type: "system",
        content: isEditing
          ? "Hi! I'm Stilton AI. I can help you edit your email template. Just tell me what changes you'd like to make!"
          : "Hi! I'm Stilton AI. I can help you create an email template. Just describe what kind of email you want to create!",
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isEditing, messages.length]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        imageMenuRef.current &&
        !imageMenuRef.current.contains(event.target as Node)
      ) {
        setShowImageMenu(false);
      }
    };

    if (showImageMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showImageMenu]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return;

    // Build the full message content with image context
    let fullMessage = inputValue.trim();

    // Append image context if there are selected images
    if (selectedImages.length > 0) {
      fullMessage += "\n\n--- Image References ---";
      selectedImages.forEach((imageUrl, index) => {
        const label = String.fromCharCode(65 + index); // A, B, C, etc.
        fullMessage += `\nImage ${label}: ${imageUrl}`;
      });
      fullMessage += "\n--- End Image References ---";
    }

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: "user",
      content: inputValue.trim(), // Display only the user's text, not the image context
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setLastUserMessage(inputValue.trim());
    setInputValue("");
    setIsProcessing(true);
    setError(null);

    try {
      let result;
      if (isEditing) {
        result = await onEdit({
          editInstructions: fullMessage, // Send the full message with image context
          currentHtmlContent,
          currentSubject,
          conversationID,
        });
      } else {
        result = await onGenerate({
          description: fullMessage, // Send the full message with image context
          conversationID,
        });
      }

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        type: "assistant",
        content:
          result.message ||
          "I've generated your email content! You can review it and click 'Apply Changes' to use it in your template.",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setLastAIResponse({
        htmlContent: result.htmlContent,
        subject: result.subject,
      });
    } catch (error) {
      console.error("Error processing AI request:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      setError(errorMessage);

      const errorResponse: Message = {
        id: `error-${Date.now()}`,
        type: "system",
        content: `Sorry, I encountered an error: ${errorMessage}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = async () => {
    if (!lastUserMessage || isProcessing) return;

    setInputValue(lastUserMessage);
    await handleSendMessage();
  };

  const handleApplyChanges = () => {
    if (lastAIResponse) {
      onApplyChanges(lastAIResponse.htmlContent, lastAIResponse.subject);
      setLastAIResponse(null);

      const confirmMessage: Message = {
        id: `system-${Date.now()}`,
        type: "system",
        content:
          "Changes applied successfully! Your template has been updated.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, confirmMessage]);
    }
  };

  const handleImageSelect = (imageUrl: string) => {
    // Check if image is already selected
    if (!selectedImages.includes(imageUrl)) {
      setSelectedImages((prev) => [...prev, imageUrl]);

      // Generate label for the new image (A, B, C, etc.)
      const nextLabel = String.fromCharCode(65 + selectedImages.length); // A=65, B=66, etc.

      // Auto-insert reference to the image in the input
      const imageText = `\n\nPlease use image ${nextLabel} in the email.`;
      setInputValue((prev) => prev + imageText);
    }

    setShowImageManager(false);
  };

  const removeSelectedImage = (urlToRemove: string) => {
    const imageIndex = selectedImages.indexOf(urlToRemove);
    const imageLabel = String.fromCharCode(65 + imageIndex);

    setSelectedImages((prev) => prev.filter((url) => url !== urlToRemove));

    // Remove references to this image from input text
    setInputValue((prev) =>
      prev
        .replace(
          new RegExp(`\\s*Please use image ${imageLabel} in the email\\.`, "g"),
          "",
        )
        .replace(
          new RegExp(
            `\\s*Please include this image: ${urlToRemove.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
            "g",
          ),
          "",
        ),
    );
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length > 0) {
      setInputValue(
        (prev) =>
          prev +
          `\n\nI want to upload and include ${imageFiles.length} image(s): ${imageFiles.map((f) => f.name).join(", ")}. Please assign them labels and let me reference them.`,
      );
    }
  };

  const handleAddImageUrl = () => {
    if (
      urlInputValue.trim() &&
      !selectedImages.includes(urlInputValue.trim())
    ) {
      const nextLabel = String.fromCharCode(65 + selectedImages.length);
      const imageText = `\n\nPlease use image ${nextLabel} in the email.`;
      setInputValue((prev) => prev + imageText);
      setSelectedImages((prev) => [...prev, urlInputValue.trim()]);
      setUrlInputValue("");
      setShowUrlInput(false);
    }
  };

  return (
    <div
      className={`relative rounded-lg border border-purple-200 bg-white shadow-sm ${isDragOver ? "border-purple-400 bg-purple-50" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-purple-100 bg-opacity-90">
          <div className="rounded-lg bg-white p-4 shadow-lg">
            <PhotoIcon className="mx-auto h-8 w-8 text-purple-600" />
            <p className="mt-2 text-sm font-medium text-purple-900">
              Drop images here to include in your request
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between border-b border-purple-200 bg-purple-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-purple-500"></div>
          <h3 className="font-medium text-purple-900">Stilton AI Assistant</h3>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-purple-600 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={messagesEndRef}
        className="max-h-96 space-y-3 overflow-y-auto p-4"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.type === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-xs rounded-lg px-3 py-2 text-sm ${
                message.type === "user"
                  ? "bg-purple-600 text-white"
                  : message.type === "system"
                    ? "bg-gray-100 text-gray-700"
                    : "bg-purple-100 text-purple-900"
              }`}
            >
              {message.content}
            </div>
          </div>
        ))}

        {isProcessing && (
          <div className="flex justify-start">
            <div className="rounded-lg bg-purple-100 px-3 py-2 text-sm text-purple-900">
              <div className="flex items-center gap-2">
                <div className="flex space-x-1">
                  <div className="h-2 w-2 animate-bounce rounded-full bg-purple-500"></div>
                  <div
                    className="h-2 w-2 animate-bounce rounded-full bg-purple-500"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="h-2 w-2 animate-bounce rounded-full bg-purple-500"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
                <span>Stilton is thinking...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Apply Changes Button */}
      {lastAIResponse &&
        lastAIResponse.htmlContent.trim() &&
        lastAIResponse.subject.trim() && (
          <div className="border-t border-purple-200 bg-purple-50 px-4 py-3">
            <button
              onClick={handleApplyChanges}
              className="w-full rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Apply Changes to Template
            </button>
          </div>
        )}

      {/* Retry Button for Empty AI Response */}
      {lastAIResponse &&
        (!lastAIResponse.htmlContent.trim() ||
          !lastAIResponse.subject.trim()) &&
        lastUserMessage && (
          <div className="border-t border-yellow-200 bg-yellow-50 px-4 py-3">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-400" />
              <div className="flex-1">
                <p className="text-sm text-yellow-700">
                  The AI response didn't generate complete content. Would you
                  like to try again?
                </p>
                <button
                  onClick={handleRetry}
                  disabled={isProcessing}
                  className="mt-2 rounded-md bg-yellow-600 px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Error with Retry */}
      {error && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
            <div className="flex-1">
              <p className="text-sm text-red-700">{error}</p>
              {lastUserMessage && (
                <button
                  onClick={handleRetry}
                  disabled={isProcessing}
                  className="mt-2 text-sm text-red-600 underline hover:text-red-800 disabled:opacity-50"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selected Images Preview */}
      {selectedImages.length > 0 && (
        <div className="border-t border-purple-200 bg-purple-50 p-4">
          <div className="mb-2 text-sm font-medium text-purple-900">
            Selected Images ({selectedImages.length})
          </div>
          <div className="mb-2 text-xs text-purple-700">
            Reference these images in your message using their labels (A, B, C,
            etc.)
          </div>
          <div className="flex flex-wrap gap-3">
            {selectedImages.map((url, index) => {
              const label = String.fromCharCode(65 + index); // A, B, C, etc.
              return (
                <div key={index} className="relative">
                  <img
                    src={url}
                    alt={`Image ${label}`}
                    className="h-16 w-16 rounded-md border border-purple-200 object-cover"
                  />
                  <div className="absolute -bottom-2 -left-2 flex h-6 w-6 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white">
                    {label}
                  </div>
                  <button
                    onClick={() => removeSelectedImage(url)}
                    className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white hover:bg-red-600"
                    title={`Remove image ${label}`}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* URL Input for Images */}
      {showUrlInput && (
        <div className="border-t border-purple-200 bg-purple-50 p-4">
          <div className="mb-2 text-sm font-medium text-purple-900">
            Add Image URL
          </div>
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInputValue}
              onChange={(e) => setUrlInputValue(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              onKeyPress={(e) => e.key === "Enter" && handleAddImageUrl()}
            />
            <button
              onClick={handleAddImageUrl}
              disabled={!urlInputValue.trim()}
              className="rounded-md bg-purple-600 px-3 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50"
            >
              Add
            </button>
            <button
              onClick={() => {
                setShowUrlInput(false);
                setUrlInputValue("");
              }}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-purple-200 p-4">
        <div className="flex gap-2">
          <div className="relative">
            <button
              onClick={() => setShowImageMenu(!showImageMenu)}
              className="flex items-center gap-1 rounded-md border border-purple-300 p-2 text-purple-600 transition-colors hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
              title="Add Images"
            >
              <PhotoIcon className="h-4 w-4" />
              <ChevronDownIcon className="h-3 w-3" />
            </button>

            {/* Image menu dropdown */}
            {showImageMenu && (
              <div
                ref={imageMenuRef}
                className="absolute bottom-full left-0 mb-2 w-48 rounded-md border border-gray-200 bg-white shadow-lg"
              >
                <button
                  onClick={() => {
                    setShowImageManager(true);
                    setShowImageMenu(false);
                  }}
                  className="flex w-full items-center gap-2 rounded-t-md px-4 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <PhotoIcon className="h-4 w-4" />
                  Browse Gallery
                </button>
                <button
                  onClick={() => {
                    setShowUrlInput(true);
                    setShowImageMenu(false);
                  }}
                  className="flex w-full items-center gap-2 rounded-b-md px-4 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <LinkIcon className="h-4 w-4" />
                  Add Image URL
                </button>
              </div>
            )}
          </div>
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder={
              isEditing
                ? "Tell me what changes you'd like to make..."
                : "Describe the email template you want to create..."
            }
            className="flex-1 resize-y rounded-md border border-gray-300 px-3 py-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
            disabled={isProcessing}
            rows={3}
            style={{ minHeight: "80px", maxHeight: "200px" }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isProcessing}
            className="rounded-md bg-purple-600 p-2 text-white transition-colors hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Press Enter to send, Shift+Enter for new line • Drag the bottom-right
          corner to resize
        </p>
      </div>

      {/* Image Manager Modal */}
      <EmailImagesManager
        open={showImageManager}
        onClose={() => setShowImageManager(false)}
        onImageSelect={handleImageSelect}
      />
    </div>
  );
}
